import { useMemo } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useLocation, useParams } from "react-router-dom";
import type { ConstantTypes, UserType } from "types/DataTypes";
import { useSelector } from "utils/redux";

export type QueryParameterType = null | string | string[];

const setQueryParameters = (
  query: URLSearchParams,
  navigate: NavigateFunction,
  paramsMap: Record<string, QueryParameterType>,
  replace: boolean = false,
): void => {
  for (const [param, value] of Object.entries(paramsMap)) {
    if (
      value === "" ||
      value === null ||
      (Array.isArray(value) && value.length === 0)
    ) {
      query.delete(param);
    } else if (Array.isArray(value)) {
      query.delete(param);
      value.forEach((v) => query.append(param, v));
    } else {
      query.set(param, value.toString());
    }
  }

  const location = window.location;
  const options = { replace };

  navigate(
    {
      pathname: location.pathname,
      search: query.toString(),
    },
    options,
  );
};

export const pushQueryParameters = (
  query: URLSearchParams,
  navigate: NavigateFunction,
  paramsMap: Record<string, QueryParameterType>,
): void => setQueryParameters(query, navigate, paramsMap, false);

export const replaceQueryParameters = (
  query: URLSearchParams,
  navigate: NavigateFunction,
  paramsMap: Record<string, QueryParameterType>,
): void => setQueryParameters(query, navigate, paramsMap, true);

export const useQuery = (): URLSearchParams => {
  const location = useLocation();
  return useMemo(
    // https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
    () => new URLSearchParams(location.search),
    [location.search],
  );
};

export const useQueryParams = (
  ...params: string[]
): Record<string, string | null | undefined> => {
  const query = useQuery();
  const queryParams = useParams();
  return params.reduce(
    (acc, param) => {
      acc[param] = query.get(param) || queryParams?.[param];
      return acc;
    },
    {} as Record<string, string | null | undefined>,
  );
};

export const useConstants = (): { constants: ConstantTypes } => {
  const constants = useSelector((state) => state.shared.constants);
  // ensures TypeScript requires proper null checking when using the constants
  return { constants } as { constants: ConstantTypes };
};

export const useCurrentUser = (): UserType | null => {
  const user = useSelector((state) => state.main.loggedInUser);
  return user;
};
